import React, { useEffect, useReducer } from 'react';
import api from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { getError } from '../utils';
import Card from 'react-bootstrap/Card';
import { Container } from 'react-bootstrap';
import Pagination from '../components/general/Pagination';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_SUCCESS':
      return {
        ...state,
        molds: action.payload.molds,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'CREATE_REQUEST':
      return { ...state, loadingCreate: true };
    case 'CREATE_SUCCESS':
      return {
        ...state,
        loadingCreate: false,
      };
    case 'CREATE_FAIL':
      return { ...state, loadingCreate: false };

    default:
      return state;
  }
};

export default function MoldsListScreen() {
  const [{ loading, error, molds, loadingCreate, pages }, dispatch] =
    useReducer(reducer, {
      loading: true,
      error: '',
      molds: [],
    });

  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const { data } = await api.get(`/api/molds/admin?page=${page}`);

        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    }; // fetches the data of all molds from the backend

    fetchData();
  }, [page]);

  const createHandler = async () => {
    if (window.confirm('Do you really want to create a new mold?')) {
      // if the user confirms that he wants to create a new mold, the following code will run
      try {
        dispatch({ type: 'CREATE_REQUEST' });
        const { data } = await api.post('/api/molds', {}); // posts new mold to the '/api/molds' backend api
        toast.success('Mold created.');
        dispatch({ type: 'CREATE_SUCCESS' });
        navigate(`/admin/mold/${data.mold._id}`); // navigates the user to the newly created mold
      } catch (err) {
        toast.error(getError(err));
        dispatch({
          type: 'CREATE_FAIL',
        });
      }
    }
  };

  return (
    <Container>
      <div>
        <title>Molds</title>

        <h1 className="screen-heading">Molds</h1>

        {loading || loadingCreate ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <>
            <Row>
              <Col>
                <div className="d-flex justify-content-center mb-5">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={createHandler}
                  >
                    Create Mold
                  </Button>
                </div>
              </Col>
            </Row>
            <Row className="d-xl-none">
              {molds?.map((mold) => (
                <Col key={mold._id} xs={12} sm={12} md={12} lg={6}>
                  <Card className="products-card">
                    <Card.Body className="text-center">
                      <Card.Title className="code mb-4">{mold.name}</Card.Title>
                      <div className="divider"></div>
                      <div className="code-info">
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Profile:
                          </strong>{' '}
                          <span className="no-wrap">{mold.profile}</span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Size:
                          </strong>{' '}
                          <span className="no-wrap">{mold.size}</span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Manufacturer:
                          </strong>{' '}
                          <span className="no-wrap">
                            {mold.keycapManufacturer}
                          </span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Active:
                          </strong>{' '}
                          <span className="no-wrap">
                            {mold.isActive ? 'Yes' : 'No'}
                          </span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Options:
                          </strong>
                          <span className="no-wrap w-100 mt-1">
                            <Button
                              variant="secondary"
                              type="button"
                              onClick={() =>
                                navigate(`/admin/mold/${mold._id}`)
                              }
                            >
                              Edit
                            </Button>
                            &nbsp;
                            <Button
                              variant="secondary"
                              type="button"
                              onClick={() => {
                                toast.info(
                                  'This button is disabled, to prevent unintended deletion of the mold document.'
                                );
                              }}
                            >
                              Delete
                            </Button>
                          </span>
                        </Card.Text>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            <div className="d-none d-xl-block">
              <table className="table">
                <thead>
                  <tr>
                    <th>NAME</th>
                    <th>PROFILE</th>
                    <th>SIZE</th>
                    <th>MANUFACTURER</th>
                    <th>ACTIVE</th>
                    <th>OPTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {molds?.map((mold) => (
                    <tr key={mold._id}>
                      <td>{mold.name}</td>
                      <td>{mold.profile}</td>
                      <td>{mold.size}</td>
                      <td>{mold.keycapManufacturer}</td>
                      <td>{mold.isActive ? 'Yes' : 'No'}</td>
                      <td>
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => navigate(`/admin/mold/${mold._id}`)}
                        >
                          Edit
                        </Button>
                        &nbsp;
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => {
                            toast.info(
                              'This button is disabled, to prevent unintended deletion of the mold document.'
                            );
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              pages={pages}
              page={Number(page)}
              baseUrl="/admin/molds"
              search={null}
            />
          </>
        )}
      </div>
    </Container>
  );
}
